import React, { useState, useEffect } from 'react';
import { getAdminBroadcasts, createNotification } from '../../services/notificationService';
import { getAllEmployees } from '../../services/adminService';
import { Send, Bell, Users, AlertCircle } from 'lucide-react';
import './NotificationsAdmin.css';

const NotificationsAdmin = () => {
    const [notifications, setNotifications] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        message: '',
        targetType: 'all',
        targetEmployees: []
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [notifsData, empsData] = await Promise.all([
                getAdminBroadcasts(),
                getAllEmployees()
            ]);
            setNotifications(notifsData);
            setEmployees(empsData);
        } catch (err) {
            console.error('Failed to fetch data', err);
            setError('Failed to load data. Please refresh.');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        const d = new Date(dateString);
        const DD = String(d.getDate()).padStart(2, '0');
        const MM = String(d.getMonth() + 1).padStart(2, '0');
        const YYYY = d.getFullYear();
        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const HH = String(hours).padStart(2, '0');
        return `${DD}:${MM}:${YYYY}, ${HH}:${minutes} ${ampm}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (employeeId) => {
        setFormData(prev => {
            const currentSelected = prev.targetEmployees;
            if (currentSelected.includes(employeeId)) {
                return {
                    ...prev,
                    targetEmployees: currentSelected.filter(id => id !== employeeId)
                };
            } else {
                return {
                    ...prev,
                    targetEmployees: [...currentSelected, employeeId]
                };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.title || !formData.message) {
            setError('Title and message are required.');
            return;
        }

        if (formData.targetType === 'specific' && formData.targetEmployees.length === 0) {
            setError('Please select at least one employee.');
            return;
        }

        try {
            setSubmitting(true);
            await createNotification(formData);
            
            // Re-fetch to get populated fields
            fetchInitialData();

            setFormData({
                title: '',
                message: '',
                targetType: 'all',
                targetEmployees: []
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send notification');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="ems-admin-page">
            <div className="ems-header-section">
                <h1 className="ems-page-title">Broadcast Notifications</h1>
                <p className="ems-page-subtitle">Send announcements and alerts to your employees.</p>
            </div>

            {error && (
                <div style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertCircle size={20} />
                    {error}
                </div>
            )}

            <div className="notifications-content-layout">
                {/* Left side: Form */}
                <form className="notification-form-card" onSubmit={handleSubmit}>
                    <h2 className="notification-form-title">New Notification</h2>

                    <div className="notification-form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="notification-form-input"
                            placeholder="e.g. Company Townhall Meeting"
                            required
                        />
                    </div>

                    <div className="notification-form-group">
                        <label>Message</label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            className="notification-form-textarea"
                            placeholder="Enter the notification details here..."
                            required
                        />
                    </div>

                    <div className="notification-form-group">
                        <label>Send To</label>
                        <div className="radio-group">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="targetType"
                                    value="all"
                                    checked={formData.targetType === 'all'}
                                    onChange={handleInputChange}
                                />
                                All Employees
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="targetType"
                                    value="specific"
                                    checked={formData.targetType === 'specific'}
                                    onChange={handleInputChange}
                                />
                                Specific Employees
                            </label>
                        </div>
                    </div>

                    {formData.targetType === 'specific' && (
                        <div className="notification-form-group">
                            <label>Select Employees</label>
                            <input
                                type="text"
                                placeholder="Search employees by name, email, or ID..."
                                className="notification-form-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ marginBottom: '0.5rem' }}
                            />
                            <div className="employee-checkbox-list">
                                {employees
                                    .filter(emp => {
                                        const term = searchTerm.toLowerCase();
                                        return (
                                            emp.name?.toLowerCase().includes(term) ||
                                            emp.email?.toLowerCase().includes(term) ||
                                            emp.employeeId?.toLowerCase().includes(term)
                                        );
                                    })
                                    .map(emp => (
                                        <label key={emp._id} className="employee-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.targetEmployees.includes(emp._id)}
                                                onChange={() => handleCheckboxChange(emp._id)}
                                            />
                                            <span>{emp.name} | {emp.email} {emp.employeeId ? `(${emp.employeeId})` : ''}</span>
                                        </label>
                                    ))}
                            </div>
                        </div>
                    )}

                    <button type="submit" className="notification-submit-btn" disabled={submitting}>
                        <Send size={18} />
                        {submitting ? 'Sending...' : 'Broadcast Notification'}
                    </button>
                </form>

                {/* Right side: List */}
                <div className="notification-list-card">
                    <h2 className="notification-list-title">Sent Notifications</h2>
                    
                    {loading ? (
                        <div className="loading-spinner">Loading...</div>
                    ) : notifications.length === 0 ? (
                        <div className="empty-state">
                            <Bell size={48} />
                            <p>No notifications have been sent yet.</p>
                        </div>
                    ) : (
                        <ul className="notification-list-items">
                            {notifications.map((notif) => (
                                <li key={notif._id} className="notification-item">
                                    <div className="notification-item-header">
                                        <h3 className="notification-item-title">{notif.title}</h3>
                                        <span className="notification-item-date">
                                            {formatDateTime(notif.createdAt)}
                                        </span>
                                    </div>
                                    <p className="notification-item-message">{notif.message}</p>
                                    
                                    <div className="notification-item-footer">
                                        <span className={`broadcast-target-badge ${notif.targetType}`}>
                                            {notif.targetType === 'all' ? (
                                                <><Users size={12} style={{marginRight: 4, verticalAlign: 'text-bottom'}}/> All Employees</>
                                            ) : (
                                                <>{notif.targetEmployees.length} Specific Employee(s)</>
                                            )}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsAdmin;
